import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Resource } from '../../../types';
import { QuizConfigurationForm } from './QuizConfigurationForm';
import { InteractiveDiagramConfigurationForm } from './InteractiveDiagramConfigurationForm';
import { SimulationConfigurationForm } from './SimulationConfigurationForm';
import { BasicResourceConfigurationForm } from './BasicResourceConfigurationForm';
// GPF Configuration Forms
import { SortingCanvasConfigForm } from './SortingCanvasConfigForm';
import { SpatialCanvasConfigForm } from './SpatialCanvasConfigForm';
import { DrawingBoardConfigForm } from './DrawingBoardConfigForm';
import { ObjectManipulatorConfigForm } from './ObjectManipulatorConfigForm';
import { MemoryGameConfigurationForm } from './MemoryGameConfigurationForm';
import { SortingGameConfigurationForm } from './SortingGameConfigurationForm';
import { TopologyCanvasConfigurationForm } from './TopologyCanvasConfigurationForm';
import PatternMakerConfigurationForm from './PatternMakerConfigurationForm';
import { ShapeRecognitionConfigurationForm } from './ShapeRecognitionConfigurationForm';
import { SizeComparisonConfigurationForm } from './SizeComparisonConfigurationForm';
import { AdditionManipulatorConfigurationForm } from './AdditionManipulatorConfigurationForm';
import EvaluationDashboardConfigurationForm from './EvaluationDashboardConfigurationForm';

interface ResourceConfigurationPanelProps {
  resource: Resource;
  resourceId: string;
  onUpdate: (resourceId: string, updates: Partial<Resource>) => void;
  onClose: () => void;
  inline?: boolean;
}

export const ResourceConfigurationPanel: React.FC<ResourceConfigurationPanelProps> = ({
  resource,
  resourceId,
  onUpdate,
  onClose,
  inline = false
}) => {
  const { t } = useTranslation();
  const updateResource = (updates: Partial<Resource>) => {
    onUpdate(resourceId, updates);
  };

  const renderConfigurationForm = () => {
    switch (resource.type) {
      case 'quiz':
        return (
          <QuizConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      case 'interactive_diagram':
        return (
          <InteractiveDiagramConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      case 'simulation':
        return (
          <SimulationConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      // GPF Resource Types
      case 'sorting_canvas':
        return (
          <SortingCanvasConfigForm
            resource={resource}
            onConfigChange={updateResource}
          />
        );
      case 'spatial_canvas':
        return (
          <SpatialCanvasConfigForm
            resource={resource}
            onConfigChange={updateResource}
          />
        );
      case 'topology_canvas':
        return (
          <TopologyCanvasConfigurationForm
            config={resource}
            onChange={updateResource}
          />
        );
      case 'pattern_maker':
        return (
          <PatternMakerConfigurationForm
            config={resource as any}
            onChange={updateResource}
          />
        );
      case 'shape_recognition':
        return (
          <ShapeRecognitionConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      case 'size_comparison':
        return (
          <SizeComparisonConfigurationForm
            config={resource}
            onChange={updateResource}
          />
        );
      case 'addition_manipulator':
        return (
          <AdditionManipulatorConfigurationForm
            config={resource}
            onChange={updateResource}
          />
        );
      case 'evaluation_dashboard':
        return (
          <EvaluationDashboardConfigurationForm
            config={resource as any}
            onChange={updateResource}
          />
        );
      case 'drawing_board':
        return (
          <DrawingBoardConfigForm
            resource={resource}
            onConfigChange={updateResource}
          />
        );
      case 'object_manipulator':
        return (
          <ObjectManipulatorConfigForm
            resource={resource}
            onConfigChange={updateResource}
          />
        );
      case 'memory_game':
        return (
          <MemoryGameConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      case 'sorting_game':
        return (
          <SortingGameConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
      default:
        return (
          <BasicResourceConfigurationForm
            resource={resource}
            onUpdate={updateResource}
          />
        );
    }
  };

  return (
    <motion.div
      initial={inline ? { opacity: 0, height: 0 } : { opacity: 0, y: 20 }}
      animate={inline ? { opacity: 1, height: "auto" } : { opacity: 1, y: 0 }}
      exit={inline ? { opacity: 0, height: 0 } : { opacity: 0, y: 20 }}
      className={inline ? "bg-white" : "border border-gray-200 rounded-lg bg-white shadow-sm"}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('resourceConfigurationPanel.configure')} {resource.type}</h3>
          <p className="text-sm text-gray-600">{t('resourceConfigurationPanel.id')}: {resourceId}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Configuration Form */}
      <div className="p-4">
        {renderConfigurationForm()}
      </div>
    </motion.div>
  );
};
